import { useLayoutEffect, useEffect, useState, useMemo, useCallback, useRef } from "react"
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { motion, AnimatePresence } from "framer-motion";

import useAppStore from "../../store/useAppStore"
import useLayoutStore from "../../store/useLayoutStore"

import LoadingSpinner from "../elements/LoadingSpinner";

const LOG_ROW_FLASH_MS = 520

const KarnetFormView = () => {
    const isDarkTheme = useLayoutStore(state => state.isDarkTheme)

    const activeThemeClass = isDarkTheme ? 'dark-color' : 'white-color'

    const karnetData = useAppStore(state => state.karnetData)
    const setKarnetData = useAppStore(state => state.setKarnetData)

    useLayoutEffect(() => {
        if (!karnetData) {
            setKarnetData(new Date().getMonth(), new Date().getFullYear())
        }
    }, [])

    return (
        <div className="view-container">
            <div className="view-header-container">
                <div className="view-header-top">
                    <div className={`v-header-name ${activeThemeClass}`}>Karnet forma</div>
                    <div className={`v-header-description ${isDarkTheme ? "dark-color" : ""}`}>Prikaz karnet tabele za tekući mesec. Izborom meseca i godine, učitavaju se pripadajući podaci. Moguće je zaokruživanje podataka na 30 minuta ili ceo sat.</div>
                </div>
            </div>
            <AnimatePresence mode="wait">
                {karnetData === null ? (
                    <LoadingSpinner />
                ) : (
                    <motion.div
                        key="content"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.3 }}
                    >
                        <div className="kv-content-container">
                            <div className="kv-content-div">
                                <ViewKarnetToolbar />
                                <VIewKarnetTable />
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    )
}

export default KarnetFormView

const VIewKarnetTable = () => {
    const isDarkTheme = useLayoutStore(state => state.isDarkTheme)

    const filteredKarnetData = useAppStore(state => state.filteredKarnetData)

    const karnetRoundingInterval = useAppStore(state => state.karnetRoundingInterval)

    const karnetFilter = useAppStore(state => state.karnetFilter)
    const loadingKarnetData = useAppStore(state => state.loadingKarnetData)
    const karnetRowFlashUserId = useAppStore(state => state.karnetRowFlashUserId)
    const karnetRowFlashTick = useAppStore(state => state.karnetRowFlashTick)

    const [tooltip, setTooltip] = useState({ show: false, content: "", x: 0, y: 0 })

    const currentCellRef = useRef(null)
    const monthKey = `${karnetFilter.year}-${karnetFilter.month}`
    const [flashingRowId, setFlashingRowId] = useState(null)

    useEffect(() => {
        setFlashingRowId(null)
    }, [monthKey, karnetRoundingInterval])

    useEffect(() => {
        if (!karnetRowFlashUserId) return undefined
        setFlashingRowId(String(karnetRowFlashUserId))
        const timer = window.setTimeout(() => setFlashingRowId(null), LOG_ROW_FLASH_MS)
        return () => window.clearTimeout(timer)
    }, [karnetRowFlashUserId, karnetRowFlashTick])

    const headers = useMemo(() => {
        if (!filteredKarnetData || loadingKarnetData) return []
        const { month, year } = karnetFilter
        const daysInMonth = new Date(year, month + 1, 0).getDate()
        return ["Ime", "Prezime", ...Array.from({ length: daysInMonth }, (_, i) => i + 1)]
    }, [filteredKarnetData])

    const rows = useMemo(() => {
        if (!filteredKarnetData || loadingKarnetData) {
            return []
        }

        const { month, year } = karnetFilter
        const daysInMonth = new Date(year, month + 1, 0).getDate()

        return Object.entries(filteredKarnetData).map(([id, user]) => ({
            id,
            ime: user.ime,
            prezime: user.prezime,
            days: Array.from({ length: daysInMonth }, (_, i) => {
                const day = (i + 1).toString() // Konvertujemo dan u string
                const value = user.days[day]

                if (!value) return { display: "", formatted: "" }

                // Vraćamo odgovarajuću vrednost na osnovu intervala
                return {
                    display: value[karnetRoundingInterval],
                    formatted: value.formatted
                }
            })
        }))
    }, [filteredKarnetData, karnetRoundingInterval])

    const showTooltip = (e, content, cellId) => {
        // Ažuriramo ref sa trenutnom ćelijom
        currentCellRef.current = cellId

        setTooltip({
            show: true,
            content,
            x: e.clientX,
            y: e.clientY
        })
    }


    const hideTooltip = (cellId) => {
        // Sakrivamo tooltip samo ako je za trenutnu ćeliju
        if (currentCellRef.current === cellId) {
            setTooltip(prev => ({ ...prev, show: false }))
            currentCellRef.current = null
        }
    }

    return (
        <AnimatePresence mode="wait">
            {loadingKarnetData ? (
                <LoadingSpinner />
            ) : (
                <motion.div
                    key={monthKey}
                    className="kv-table-container"
                    initial={{ opacity: 0, x: 40 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.19, ease: "easeOut" }}
                >
                    <table className={`kv-table ${isDarkTheme ? 'dark-table' : ''} `}>
                        <thead>
                            <tr>
                                {headers.map((h, idx) => (
                                    <th key={idx}>{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map(row => {
                                const isFlashing = String(row.id) === String(flashingRowId)

                                return (
                                <tr
                                    key={row.id}
                                    className={`asv-emp-day-log-row${isFlashing ? " asv-emp-day-log-row--flash" : ""}`}
                                >
                                    <td>{row.ime}</td>
                                    <td>{row.prezime}</td>
                                    {row.days.map((dayData, idx) => (
                                        <td
                                            key={idx}
                                            onMouseEnter={(e) => {
                                                const header = document.querySelector(
                                                    `.kv-table thead th:nth-child(${idx + 3})`
                                                )
                                                header?.classList.add("hover-col")
                                                showTooltip(e, dayData.formatted)
                                            }}
                                            onMouseLeave={() => {
                                                const header = document.querySelector(
                                                    `.kv-table thead th:nth-child(${idx + 3})`
                                                )
                                                header?.classList.remove("hover-col")
                                                hideTooltip()
                                            }}
                                        >
                                            {dayData.display}
                                        </td>
                                    ))}
                                </tr>
                                )
                            })}
                        </tbody>
                    </table>
                    <AnimatePresence mode="wait">
                        {tooltip.show && (
                            <motion.div
                                key={currentCellRef.current}
                                className={`kv-table-custom-tooltip ${isDarkTheme ? 'dark-table-tooltip' : ''}`}
                                initial={{ opacity: 0, scale: 0.8 }}
                                animate={{ opacity: 1, scale: 1 }}
                                exit={{ opacity: 0, scale: 0.8 }}
                                transition={{ duration: 0.14, ease: "easeOut" }}
                                style={{
                                    position: 'fixed',
                                    left: tooltip.x + 14,
                                    top: tooltip.y + 19,
                                    background: '#afafafff',
                                    color: '#fff',
                                    padding: '8px 12px',
                                    borderRadius: '6px',
                                    fontSize: '14px',
                                    zIndex: 1000,
                                    pointerEvents: 'none',
                                    transformOrigin: 'top left'
                                }}
                            >
                                {tooltip.content}
                            </motion.div>
                        )}
                    </AnimatePresence>
                </motion.div>
            )}
        </AnimatePresence>
    )
}


const ViewKarnetToolbar = () => {
    const karnetFilter = useAppStore(state => state.karnetFilter)
    const setKarnetData = useAppStore(state => state.setKarnetData)

    const isDarkTheme = useLayoutStore(state => state.isDarkTheme)

    const handleMonthChange = (month) => {
        setKarnetData(month, karnetFilter.year);
        setIsMonthOpen(false);
    };

    const handleYearChange = (year) => {
        setKarnetData(karnetFilter.month, year);
        setIsYearOpen(false);
    };

    const [isMonthOpen, setIsMonthOpen] = useState(false);
    const [isYearOpen, setIsYearOpen] = useState(false);


    return (
        <div className="kv-toolbar-container">
            <div className="kv-filters">
                {/* Mesec */}
                <div className="kv-filter-item">
                    <label className={`${isDarkTheme ? 'dark-color' : ''}`}>Mesec</label>
                    <div
                        className={`kv-dropdown ${isDarkTheme ? 'dark-dropdown' : ''}`}
                        onClick={() => setIsMonthOpen(!isMonthOpen)}
                    >
                        {months[karnetFilter.month]}
                        <motion.div
                            className="arrow"
                        >
                            <svg
                                viewBox="0 0 24 24"
                                width="24"
                                height="24"
                                style={{ display: "block" }}
                            >
                                <path
                                    d="M7 10l5 5 5-5"
                                    fill="none"
                                    stroke={isDarkTheme ? "rgb(219, 219, 219)" : "black"}
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                        </motion.div>
                        <AnimatePresence>
                            {isMonthOpen && (
                                <motion.div
                                    className={`kv-dropdown-list ${isDarkTheme ? 'dark-dropdownlist' : ''}`}
                                    initial={{ opacity: 0, y: -10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -10 }}
                                    transition={{ duration: 0.14, ease: "easeOut" }}
                                >
                                    {months.map((m, idx) => (
                                        <div
                                            key={idx}
                                            className={`kv-dropdown-item ${isDarkTheme ? 'dark-dropdownitem' : ''}`}
                                            onClick={() => handleMonthChange(idx)}
                                        >
                                            {m}
                                        </div>
                                    ))}
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>
                </div>

                {/* Godina */}
                <div className="kv-filter-item">
                    <label className={`${isDarkTheme ? 'dark-color' : ''}`}>Godina</label>
                    <div
                        className={`kv-dropdown ${isDarkTheme ? 'dark-dropdown' : ''}`}
                        onClick={() => setIsYearOpen(!isYearOpen)}
                    >
                        {karnetFilter.year}
                        <motion.div
                            className="arrow"
                        >
                            <svg
                                viewBox="0 0 24 24"
                                width="24"
                                height="24"
                                style={{ display: "block" }}
                            >
                                <path
                                    d="M7 10l5 5 5-5"
                                    fill="none"
                                    stroke={isDarkTheme ? "rgb(219, 219, 219)" : "black"}
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                        </motion.div>
                        <AnimatePresence>
                            {isYearOpen && (
                                <motion.div
                                    className={`kv-dropdown-list ${isDarkTheme ? 'dark-dropdownlist' : ''}`}
                                    initial={{ opacity: 0, y: -10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -10 }}
                                    transition={{ duration: 0.14, ease: "easeOut" }}
                                >
                                    {Array.from({ length: 6 }, (_, i) => {
                                        const y = new Date().getFullYear() - i;
                                        return (
                                            <div
                                                key={y}
                                                className={`kv-dropdown-item ${isDarkTheme ? 'dark-dropdownitem' : ''}`}
                                                onClick={() => handleYearChange(y)}
                                            >
                                                {y}
                                            </div>
                                        );
                                    })}
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>
                </div>
            </div>
            <KarnetToolbarActions />
        </div>
    );
};

const KarnetToolbarActions = () => {
    const karnetFilter = useAppStore(state => state.karnetFilter)
    const karnetRoundingInterval = useAppStore(state => state.karnetRoundingInterval)
    const setKarnetRoundingInterval = useAppStore(state => state.setKarnetRoundingInterval)

    const isDarkTheme = useLayoutStore(state => state.isDarkTheme)

    const handleExportExcel = useCallback(() => {
        const { filteredKarnetData } = useAppStore.getState(); // <-- povlačimo sveže podatke
        if (!filteredKarnetData) return;

        const { month, year } = karnetFilter;
        const daysInMonth = new Date(year, month + 1, 0).getDate();

        // Header
        const headers = ["Ime", "Prezime", ...Array.from({ length: daysInMonth }, (_, i) => i + 1)];

        // Rows
        const rows = Object.entries(filteredKarnetData).map(([id, user]) => {
            return [
                user.ime,
                user.prezime,
                ...Array.from({ length: daysInMonth }, (_, i) => {
                    const day = (i + 1).toString(); // Dan kao string
                    const dayData = user.days[day];

                    if (!dayData) return ""; // Ako nema podataka za taj dan

                    // Koristimo trenutno selektovani rounding interval
                    return dayData[karnetRoundingInterval];
                }),
            ];
        });

        // Worksheet & Workbook
        const worksheet = XLSX.utils.aoa_to_sheet([headers, ...rows]);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Karnet");

        // Save file
        const fileName = `karnet_${months[month]}_${year}.xlsx`;
        const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
        saveAs(new Blob([excelBuffer], { type: "application/octet-stream" }), fileName);
    }, [karnetFilter, karnetRoundingInterval]);

    const handleChangeInterval = (e) => {
        const target = e.target.closest('[data-name]');
        if (!target) return;
        const name = target.getAttribute('data-name');
        // if (name === karnetRoundingInterval) {
        //     setKarnetRoundingInterval('realTime')
        //     return
        // }
        setKarnetRoundingInterval(name)
    }

    return (
        <div className="kv-tools-container">
            <div className="kv-tools-div" onClick={handleChangeInterval}>
                <KarnetHeaderButton
                    name="round30min"
                    icon="./views/30min.svg"
                    active={karnetRoundingInterval === 'round30min'}
                    tooltip="Zaokružiti na 30min"
                />
                <KarnetHeaderButton
                    name="round1h"
                    icon={"./views/1h.svg"}
                    active={karnetRoundingInterval === 'round1h'}
                    tooltip="Zaokružiti na 1h"
                />
            </div>
            <button className={`kv-xlsx-button ${isDarkTheme ? 'dark-big-btn ' : ''}`} onClick={handleExportExcel}>
                Generiši XLSX
            </button>
        </div>
    )
}

const KarnetHeaderButton = ({ name, active, icon, tooltip }) => {
    const isTouchDevice = useLayoutStore(state => state.isTouchDevice)
    const [isHovered, setIsHovered] = useState(false);

    const isDarkTheme = useLayoutStore(state => state.isDarkTheme)
    const activeClass = isDarkTheme ? 'dark-active' : 'active'

    return (
        <div className="v-header-button-div">
            <button
                className={`v-header-icon-button ${active ? activeClass : ""}`}
                // className="v-header-icon-button"
                data-name={name}
                onMouseEnter={() => !isTouchDevice && setIsHovered(true)}
                onMouseLeave={() => !isTouchDevice && setIsHovered(false)}
            >
                <img src={icon} alt={name} className={`${isDarkTheme ? 'dark-filter' : ''}`} />
            </button>
            <AnimatePresence>
                {isHovered && (
                    <motion.div
                        className={`v-header-button-tooltip  ${isDarkTheme ? 'tooltip-dark' : 'tooltip-white'}`}
                        initial={{ opacity: 0, y: -14 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -14 }}
                        transition={{ duration: 0.19, ease: "easeOut" }}
                    >
                        {tooltip}
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}

const months = [
    "Januar", "Februar", "Mart", "April", "Maj", "Jun",
    "Jul", "Avgust", "Septembar", "Oktobar", "Novembar", "Decembar"
];